/**
 * Campus: home campus and the campus this person serves at (LocationManagement ids).
 * LocationID 0 is "Online Campus", a real campus, so ids are compared with null, never with `!id`.
 * Self: GET/PUT /me/locations. Admin: GET/PUT /{memberId}/locations.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconBuildingChurch, IconDeviceFloppy, IconAlertCircle, IconHeartHandshake } from '@tabler/icons-react';
import { Card, Stack, Group, Button, Select, SimpleGrid, Text, Alert, Skeleton, ThemeIcon, Switch } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import type { MemberTabProps } from '../types';
import { useCampuses } from '../useMemberLookups';
import { useInvalidateMember } from '../useMemberMutations';
import { errorMessage, locationValue, parseLocationValue } from '../memberFormat';

export function CampusTab({ api, perms }: MemberTabProps) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateMember(api);
  const locations = useQuery({ queryKey: api.keys.locations, queryFn: () => api.getLocations() });
  const campuses = useCampuses();

  const current = locations.data?.[0] ?? null;
  const initialHome = locationValue(current?.locationID);
  const initialServe = locationValue(current?.serveLocationID);

  const [home, setHome] = useState<string | null>(initialHome);
  const [serve, setServe] = useState<string | null>(initialServe);
  const [sameAsHome, setSameAsHome] = useState(true);

  useEffect(() => {
    setHome(initialHome);
    setServe(initialServe);
    setSameAsHome(initialServe === null || initialServe === initialHome);
  }, [initialHome, initialServe]);

  // A campus that was archived (or is missing from the active list) still shows by name.
  const options = useMemo(() => {
    const opts = [...campuses.options];
    for (const row of locations.data ?? []) {
      for (const [id, name] of [[row.locationID, row.locationName], [row.serveLocationID, row.serveLocationName]] as const) {
        if (id !== null && id !== undefined && !opts.some((o) => o.value === String(id))) {
          opts.push({ value: String(id), label: name || `Campus ${id}` });
        }
      }
    }
    return opts;
  }, [campuses.options, locations.data]);

  const nameFor = (value: string | null, fallback?: string | null) =>
    value === null ? null : options.find((o) => o.value === value)?.label ?? fallback ?? `Campus ${value}`;

  const effectiveServe = sameAsHome ? home : serve;
  const dirty = home !== initialHome || (home !== null && effectiveServe !== (initialServe ?? initialHome));

  const save = useMutation({
    mutationFn: () => api.setLocation({
      locationID: parseLocationValue(home),
      serveLocationID: home === null ? null : parseLocationValue(effectiveServe),
    }),
    onSuccess: () => {
      toast.success('Saved', home === null ? 'Campus removed.' : 'Campus updated.');
      queryClient.invalidateQueries({ queryKey: api.keys.locations });
      invalidate();
    },
    onError: (e) => toast.error('Could not save the campus', errorMessage(e)),
  });

  const readOnly = !perms.campusWrite;

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <div>
          <Text fw={600}>Campus</Text>
          <Text size="sm" c="dimmed">
            {api.mode === 'self' ? 'Where you usually attend and where you serve.' : 'Where this person usually attends and serves.'}
          </Text>
        </div>

        {locations.isLoading && <Skeleton height={90} />}
        {locations.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the campus">{errorMessage(locations.error)}</Alert>}
        {campuses.isError && <Alert color="yellow" icon={<IconAlertCircle size={16} />}>The campus list could not be loaded, so names may be missing.</Alert>}

        {!locations.isLoading && !locations.isError && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Card withBorder padding="md" radius="md">
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="light" color="blue" size="lg"><IconBuildingChurch size={18} /></ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Home campus</Text>
                    <Text fw={600}>{nameFor(initialHome, current?.locationName) ?? 'Not set'}</Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder padding="md" radius="md">
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="light" color="teal" size="lg"><IconHeartHandshake size={18} /></ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Serves at</Text>
                    <Text fw={600}>{nameFor(initialServe ?? initialHome, current?.serveLocationName) ?? 'Not set'}</Text>
                  </div>
                </Group>
              </Card>
            </SimpleGrid>

            {!readOnly && (
              <>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Select label="Home campus" placeholder="Choose a campus" searchable clearable data={options}
                    value={home} onChange={setHome} nothingFoundMessage="No campus matches" />
                  <Select label="Serves at" placeholder="Choose a campus" searchable data={options}
                    value={effectiveServe} disabled={sameAsHome || home === null} onChange={setServe} nothingFoundMessage="No campus matches" />
                </SimpleGrid>
                <Switch label="Serves at the home campus" checked={sameAsHome} disabled={home === null}
                  onChange={(e) => { setSameAsHome(e.currentTarget.checked); if (e.currentTarget.checked) setServe(home); }} />
                {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
                <Group justify="flex-end">
                  <Button variant="default" disabled={!dirty || save.isPending}
                    onClick={() => { setHome(initialHome); setServe(initialServe); setSameAsHome(initialServe === null || initialServe === initialHome); }}>
                    Discard changes
                  </Button>
                  <Button leftSection={<IconDeviceFloppy size={16} />} loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
                    Save campus
                  </Button>
                </Group>
              </>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}
